import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Icon } from '@unite-us/app-components';
import DialogV2 from 'common/modal/DialogV2';
import IconButton from 'common/buttons/IconButton/IconButton';
import EditNoteForm from '../EditNoteForm';
import { NOTE_VISIBILITY } from '../constants';
import useNoteDeletion from '../hooks/useNoteDeletion';
import {
  canDeleteNote,
  getInteractionDateParts,
  getNoteDate,
  getProcessedNoteMetadata,
  getServiceProvidedData,
  shouldShowDeleteButton,
  shouldShowEditButton,
  shouldShowReason,
} from '../utils/noteUtils';
import NoteDeleteContent from './NoteDeleteContent';
import SideDate from './SideDate';

const ProcessedNoteHeader = ({
  added,
  date,
  note,
  type,
}) => {
  if (!type || !date) {
    return null;
  }

  return (
    <p className="pb-2">
      <strong>{type}</strong>
      {added} by <strong>{get(note, 'employee.full_name', '')}</strong> on {date}
    </p>
  );
};

ProcessedNoteHeader.propTypes = {
  added: PropTypes.string,
  date: PropTypes.string,
  note: PropTypes.object.isRequired,
  type: PropTypes.string,
};

ProcessedNoteHeader.defaultProps = {
  added: null,
  date: null,
  type: null,
};

const ServiceProvidedRows = ({ serviceProvided }) => {
  if (!serviceProvided) {
    return null;
  }

  return (
    <div>
      <p className="pb-2">Service: {serviceProvided.type}</p>
      <p className="pb-2">
        Amount: {`${serviceProvided.amount} ${serviceProvided.unit}`}
      </p>
    </div>
  );
};

ServiceProvidedRows.propTypes = {
  serviceProvided: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    unit: PropTypes.string,
  }),
};

ServiceProvidedRows.defaultProps = {
  serviceProvided: null,
};

const NoteText = ({ noteType, renderTextAsHtml, text }) => (
  <>
    <strong className="font-bold pb-2">{noteType}:</strong>
    {/* eslint-disable react/no-danger */}
    {
      renderTextAsHtml ? (
        <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: text }} />
      ) : (
        <p className="whitespace-pre-wrap">{text}</p>
      )
    }
    {/* eslint-enable react/no-danger */}
  </>
);

NoteText.propTypes = {
  noteType: PropTypes.string.isRequired,
  renderTextAsHtml: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

const PrivateVisibilityNotice = ({ visibility }) => (
  visibility === NOTE_VISIBILITY.PRIVATE ? (
    <div className="inline-block flex-1 w-64 align-middle text-right text-medium-gray">
      <Icon icon="EyeDisabled" /> Private to my organization
    </div>
  ) : null
);

PrivateVisibilityNotice.propTypes = {
  visibility: PropTypes.string.isRequired,
};

const RegularNote = ({
  category,
  currentEmployeeId,
  details,
  duration,
  interactionDate,
  interactionType,
  isEditing,
  note,
  noteType,
  onCancelEdit,
  onEdit,
  reason,
  renderTextAsHtml,
  showDeleteNoteIcon,
  showEditNoteIcon,
  text,
  visibility,
}) => {
  const {
    day,
    month,
    year,
  } = getInteractionDateParts(interactionDate);

  const {
    type,
    added,
    date,
  } = getProcessedNoteMetadata(note);

  const showDeleteButton = shouldShowDeleteButton({
    currentEmployeeId,
    note,
    showDeleteNoteIcon,
  });

  const showDeleteAction = canDeleteNote({
    currentEmployeeId,
    details,
    note,
    showDeleteNoteIcon,
  });

  const showEditButton = shouldShowEditButton({
    category,
    currentEmployeeId,
    note,
    showEditNoteIcon,
  });

  const showInteractionType = interactionType !== '';
  const showDate = category !== 'general';
  const showReasonRow = shouldShowReason(reason, details);
  const serviceProvided = getServiceProvidedData(details);
  const noteDate = getNoteDate(details, category);
  const className = showDeleteButton ? 'employee_note_item' : 'note_item';
  const {
    cancelDelete,
    confirmDelete,
    deleteNoteDialogRef,
    openDeleteDialog,
  } = useNoteDeletion(note);

  if (isEditing) {
    return (
      <div className="employee_note_item employee_note_item--editing">
        <EditNoteForm note={note} onCancel={onCancelEdit} />
      </div>
    );
  }

  return (
    <div className={`flex ${className} p-3 py-4 bg-white border border-solid border-medium-border-blue rounded`}>
      <SideDate day={day} month={month} year={year} />
      <div className="inline-block flex-1 w-64 align-middle">
        <ProcessedNoteHeader
          added={added}
          date={date}
          note={note}
          type={type}
        />
        {showInteractionType ? (
          <p className="pb-2">Interaction Type: {interactionType}</p>
        ) : null}
        {showDate ? (
          <p className="pb-2">Date: {noteDate}</p>
        ) : null}
        {duration ? <p className="pb-2">Duration: {duration}</p> : null}
        <ServiceProvidedRows serviceProvided={serviceProvided} />
        {showReasonRow ? <p className="pb-2">Reason: {reason} </p> : null}
        <NoteText noteType={noteType} renderTextAsHtml={renderTextAsHtml} text={text} />
      </div>
      <PrivateVisibilityNotice visibility={visibility} />
      <div className="flex-col self-center">
        {showEditButton ? (
          <IconButton
            className={`${className}_edit-btn mb-2`}
            labelText="Edit Note"
            icon="Pencil"
            onClick={onEdit}
          />
        ) : null}
        {showDeleteAction ? (
          <IconButton
            className={`${className}_delete-btn`}
            labelText="Delete Note"
            icon="Delete"
            onClick={openDeleteDialog}
          />
        ) : null}
      </div>
      <DialogV2
        ref={deleteNoteDialogRef}
        title="Delete Note"
        confirmLabel="Delete"
        cancelHandler={cancelDelete}
        confirmationHandler={confirmDelete}
      >
        <NoteDeleteContent />
      </DialogV2>
    </div>
  );
};

RegularNote.propTypes = {
  category: PropTypes.string.isRequired,
  currentEmployeeId: PropTypes.string,
  details: PropTypes.object.isRequired,
  duration: PropTypes.string,
  interactionDate: PropTypes.object.isRequired,
  interactionType: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  note: PropTypes.object.isRequired,
  noteType: PropTypes.string.isRequired,
  onCancelEdit: PropTypes.func,
  onEdit: PropTypes.func,
  reason: PropTypes.string,
  renderTextAsHtml: PropTypes.bool.isRequired,
  showDeleteNoteIcon: PropTypes.bool.isRequired,
  showEditNoteIcon: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
};

RegularNote.defaultProps = {
  currentEmployeeId: '',
  duration: null,
  onCancelEdit: null,
  onEdit: null,
  reason: '',
};

export default RegularNote;
